"use client";

import type {
  FitnessDimensionDetail,
  FitnessDimensionType,
  FitnessScoreData,
  GoalImpactData,
} from "../types";
import { Icon } from "@ospweb/sdk-ui";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/fitness/goal/GoalFitnessImpact";
import { FitnessRadarChart } from "../RadarChart/FitnessRadarChart";
import { cn } from "utils";

function dimValue(
  dimensions: FitnessDimensionDetail[],
  code: "control" | "safety" | "growth",
): number {
  return dimensions.find((d) => d.code === code)?.value ?? 0;
}

const I18n = I18nUtil.createScoped(Translations);

/**
 * 维度显示名映射 — UI 阶段硬编码，L-phase 由 API label 替换
 *
 * 技术说明：
 * - key 对齐 FitnessDimensionType
 * - value 为 Figma 设计中的 tag 文案
 */
const DIMENSION_LABEL: Record<FitnessDimensionType, string> = {
  control: "Control",
  safety: "Safety",
  growth: "Growth",
  fluency: "Fluency",
  peace: "Peace",
};

const IMPACT_RADAR_DIMENSIONS = ["control", "safety", "growth"] as const;

/**
 * GoalFitnessImpact — Goal 对 Financial Fitness Score 的影响卡片
 *
 * 新增功能：
 * - 基于 Figma `97283:85076` — Goal Detail 中 "What this plan does for your score" 区域
 * - 显示完成该 plan 后的分数提升值 (+N) 和受影响维度
 * - delta ≤ 0 时整块隐藏 (条件渲染)
 *
 * 技术选择：
 * - UI 阶段使用 MOCK_GOAL_IMPACT 数据
 * - L-phase 从 Goal API 获取 fitness_impact 字段
 * - 条件渲染逻辑在组件内部，不渲染空占位
 *
 * @param impact - Goal impact 快照（delta + dimensions）
 */
interface GoalFitnessImpactProps {
  impact: GoalImpactData | null;
  fitnessScore?: FitnessScoreData | null;
  isLoading?: boolean;
  className?: string;
  cardClassName?: string;
  showRadar?: boolean;
  hideTitle?: boolean;
}

export function GoalFitnessImpact({
  impact,
  fitnessScore = null,
  isLoading = false,
  className,
  cardClassName,
  showRadar = false,
  hideTitle = false,
}: GoalFitnessImpactProps) {
  if (isLoading || !impact || impact.delta <= 0) {
    return null;
  }

  const highlightedDimensions = IMPACT_RADAR_DIMENSIONS.filter((dimension) =>
    impact.dimensions.includes(dimension),
  );

  return (
    <section
      className={cn("flex flex-col gap-3", className)}
      data-testid="goal-fitness-impact"
      data-node-id="97283:85483"
    >
      {!hideTitle ? (
        <h3 className="headline_regular_s text-content_primary tracking-[-0.8px]">
          {I18n.t("What this plan does for your score")}
        </h3>
      ) : null}

      <div
        className={cn(
          "flex items-center gap-4 overflow-clip rounded-2 bg-background_brand_overlay10 px-4 py-3",
          cardClassName,
        )}
        data-node-id="97283:85485"
        data-testid="goal-fitness-impact-card"
      >
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex flex-col items-start">
            <span className="text-[36px] font-semibold leading-[44px] text-content_accent1_primary">
              +{impact.delta}
            </span>
            <span className="body_s text-content_tertiary">
              {I18n.t("Potential financial fitness score")}
            </span>
          </div>

          {highlightedDimensions.length > 0 ? (
            <div className="flex flex-wrap items-center gap-[7px]">
              {highlightedDimensions.map((dim) => (
                <span
                  key={dim}
                  className="inline-flex items-center gap-0.5 rounded-1 bg-background_solid_neutral_primary py-1 pl-1.5 pr-2 body_medium_s text-content_accent1_primary"
                  data-testid={`impact-tag-${dim}`}
                >
                  <Icon
                    name="caret-up"
                    size={12}
                    className="text-content_accent1_primary"
                  />
                  {DIMENSION_LABEL[dim]}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        {showRadar && fitnessScore ? (
          <FitnessRadarChart
            total={fitnessScore.value}
            control={dimValue(fitnessScore.dimensions, "control")}
            safety={dimValue(fitnessScore.dimensions, "safety")}
            growth={dimValue(fitnessScore.dimensions, "growth")}
            size="plan-impact"
          />
        ) : null}
      </div>
    </section>
  );
}
